import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { getPropertyInfo } from "@/lib/api";
import { ClipboardCheck, ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { mascotBase64 } from "@/assets/mascotBase64";

export default function GuestCheckin() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [propertyInfo, setPropertyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formLoaded, setFormLoaded] = useState(false);
  const [error, setError] = useState(null);

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(`/${slug || "VaranasiHostel"}`);
    }
  };

  const getOptimizedFormUrl = (url) => {
    if (!url) return "";
    try {
      const urlObj = new URL(url);
      if (!urlObj.searchParams.has("embedded")) {
        urlObj.searchParams.append("embedded", "true");
      }
      return urlObj.toString();
    } catch (e) {
      return url; // fallback if invalid URL
    }
  };

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        let propertyId = slug;
        const normalizedSlug = (slug || "").toLowerCase();
        
        if (normalizedSlug.includes("varanasi")) propertyId = "varanasi";
        else if (normalizedSlug.includes("darjeeling") && normalizedSlug.includes("home")) propertyId = "darjeeling-home";
        else if (normalizedSlug.includes("darjeeling")) propertyId = "darjeeling-hostel";
        
        const data = await getPropertyInfo(propertyId);
        setPropertyInfo(data);
      } catch (err) {
        setError("Property not found");
      } finally {
        setLoading(false);
      }
    };
    fetchInfo();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#2A9D8F] to-[#48bcae] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent"></div>
      </div>
    );
  }

  if (error || !propertyInfo?.checkin_form_url) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#2A9D8F] to-[#48bcae] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <ClipboardCheck className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h2 className="text-xl font-bold text-gray-700 mb-2">Check-in Form Not Available</h2>
            <p className="text-gray-500 mb-6">
              {error || "The check-in form has not been configured for this property yet."}
            </p>
            <Button onClick={handleGoBack} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" /> Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2A9D8F] to-[#48bcae]">
      {/* Preconnect to Google domains to speed up iframe load */}
      <link rel="preconnect" href="https://docs.google.com" />
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />

      {/* Compact Header with Return Navigation */}
      <div className="bg-white/10 backdrop-blur-sm border-b border-white/20">
        <div className="max-w-4xl mx-auto px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={handleGoBack}
              className="p-2 rounded-full bg-white/15 hover:bg-white/25 text-white transition-colors flex items-center justify-center"
              title="Return to Home"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <img src={mascotBase64} alt="Hidden Monkey" className="w-12 h-12 object-contain" />
            <div>
              <h1 className="text-white font-semibold text-base">Guest Check-in</h1>
              <p className="text-white/60 text-xs">Hidden Monkey Stays</p>
            </div>
          </div>
        </div>
      </div>

      {/* Form Container */}
      <div className="max-w-4xl mx-auto p-3">
        <Card className="overflow-hidden shadow-2xl">
          <CardContent className="p-0">
            {/* Slim Info Banner */}
            <div className="bg-gradient-to-r from-[#FFC107] to-[#FFC107] px-3 py-1.5 text-white">
              <div className="flex items-center gap-2">
                <ClipboardCheck className="w-4 h-4 flex-shrink-0" />
                <p className="font-medium text-sm">Welcome! Please complete the form below to check in.</p>
              </div>
            </div>
            
            <div className="relative w-full" style={{ minHeight: "85vh" }}>
              {/* Loading skeleton positioned behind the iframe */}
              {!formLoaded && (
                <div className="absolute inset-0 z-0 flex flex-col items-center justify-center py-20 gap-4" style={{ minHeight: "60vh" }}>
                  <Loader2 className="w-10 h-10 text-[#2A9D8F] animate-spin" />
                  <p className="text-[#264653] font-medium text-lg">Loading check-in form...</p>
                  <p className="text-[#6B705C] text-sm">This may take a few seconds</p>
                  {/* Skeleton lines to hint at form structure */}
                  <div className="w-full max-w-md space-y-4 mt-6 px-6">
                    <div className="h-10 bg-gray-200 rounded-lg animate-pulse" />
                    <div className="h-10 bg-gray-200 rounded-lg animate-pulse" style={{ animationDelay: "0.1s" }} />
                    <div className="h-10 bg-gray-200 rounded-lg animate-pulse" style={{ animationDelay: "0.2s" }} />
                    <div className="h-10 bg-gray-200 rounded-lg animate-pulse" style={{ animationDelay: "0.3s" }} />
                  </div>
                </div>
              )}

              {/* Embedded Google Form directly stacked on top */}
              <iframe
                src={getOptimizedFormUrl(propertyInfo.checkin_form_url)}
                className="relative z-10"
                width="100%"
                height="100%"
                frameBorder="0"
                marginHeight="0"
                marginWidth="0"
                title="Check-in Form"
                loading="eager"
                onLoad={() => setFormLoaded(true)}
                style={{ minHeight: "85vh" }}
              >
                Loading form...
              </iframe>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-4 text-white/60 text-xs">
          <p>Need help? Contact the front desk</p>
          {propertyInfo.contact_phone && (
            <p className="mt-1">📞 {propertyInfo.contact_phone}</p>
          )}
        </div>
      </div>
    </div>
  );
}
